import React from "react";
import { User } from "./User";

export type ModifiableUserProfileField = keyof Pick<
  User,
  "firstName" | "lastName" | "currencyCode" | "activePersona"
>;

export class MfaRequiredError extends Error {
  readonly username: string;
  readonly password: string;
  readonly remember: boolean;
  constructor(username: string, password: string, remember: boolean) {
    super("MFA token required");
    this.name = "MfaRequiredError";
    this.username = username;
    this.password = password;
    this.remember = remember;
  }
}

/** The server knows no user with this username or email and this password. */
export class InvalidCredentialsError extends Error {
  constructor() {
    super("Invalid username or password");
    this.name = "InvalidCredentialsError";
  }
}

/**
 * Who is signed in. A session the server rejected as invalid leaves nobody
 * signed in, as a logout does, but the sign-in page tells the user why they
 * landed there. (Agent-written comment.)
 */
export type AuthState =
  | { kind: "signedIn"; user: User }
  | { kind: "signedOut" }
  | { kind: "sessionExpired" };

export interface AuthContextProps {
  auth: AuthState;
  login: (
    username: string,
    password: string,
    remember: boolean,
  ) => Promise<void>;
  loginWithMfa: (
    username: string,
    password: string,
    token: string,
    remember: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateUserField(key: ModifiableUserProfileField, value: any): Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  enrolMfa: (
    secret: string,
    token: string,
  ) => Promise<{ recoveryCodes: string[] }>;
  confirmMfa: () => Promise<void>;
  disableMfa: (currentToken: string) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextProps>({
  auth: { kind: "signedOut" },
  login: async () => {
    throw new Error("Not initialized");
  },
  loginWithMfa: async () => {
    throw new Error("Not initialized");
  },
  logout: async () => {
    throw new Error("Not initialized");
  },
  updateUserField: async () => {
    throw new Error("Not initialized");
  },
  changePassword: async () => {
    throw new Error("Not initialized");
  },
  enrolMfa: async () => {
    throw new Error("Not initialized");
  },
  confirmMfa: async () => {
    throw new Error("Not initialized");
  },
  disableMfa: async () => {
    throw new Error("Not initialized");
  },
});
